import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader, Spinner, useApiMutation } from '@/modules/common';
import { useTableStore } from '@/modules/common/store';
import { Routes } from '@/router/routes';

import { useEventDetails } from '../api';
import { EventForm, EventFormSchemaType } from '../components';
import { formatModelDetails } from '../helpers';
import { UpdateEventPayload } from '../types';

export const EventDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    isFetching,
    refetchUploadUrl,
    uploadUrlData,
    updateEventMutation,
    refetchIdleStateUploadUrl,
  } = useEventDetails(params.id);
  const { setSearchValue } = useTableStore();

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      masks: details.masks.map((mask) => ({
        maskId: mask.id,
        overlayId: mask.overlays?.[0]?.id ?? '',
      })),
      idleStateVideoPublicUrl: details.idleStateVideoPublicUrl || '',
      styles: details.styles.map((style) => ({ styleId: style.id })),
      updatedAt: format(new Date(details.startDate), 'yyyy-MM-dd'),
      models: details.models.map(formatModelDetails).flat(),
    };
  }, [details]);

  const removeIdleStateVideoMutation = useApiMutation<void>(
    `/events/${params.id}/idle-state-video`,
    {
      method: 'DELETE',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const updateEvent = async (data) => {
    try {
      const body = deepDifference<UpdateEventPayload>(data, defaultValues);

      if (
        body.hasOwnProperty('idleStateVideoPublicUrl') &&
        !body.idleStateVideoPublicUrl
      ) {
        await removeIdleStateVideoMutation.mutateAsync();
      }

      await updateEventMutation.mutateAsync({
        ...body,
        masks: data.masks.map((mask) => ({
          maskId: mask.maskId,
          overlays: mask.overlayId ? [{ overlayId: mask.overlayId }] : [],
        })),
        styles: data.styles,
        models: data.models,
        printers: data.printers,
        id: params.id,
      });
      toast.success(`Event “${data.name}” updated successfully!`);
    } catch (error) {
      console.error(error);
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
      await refetchIdleStateUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  const handleBack = () => {
    setSearchValue('');
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <EventForm
      editMode
      eventDetails={details}
      onSubmit={updateEvent}
      uploadUrlData={uploadUrlData}
      refetchUploadCredentials={refetchUploadCredentials}
      defaultValues={defaultValues as unknown as EventFormSchemaType}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          onBack={handleBack}
          backPath={Routes.Events}
          actions={[
            <Button
              key="save-event"
              data-testid="save-event-button"
              type="submit"
              isLoading={
                isFetching || isLoading || updateEventMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </EventForm>
  );
};
